/**
 * API клиент для работы с Go backend
 */

import type {
  ApiResponse,
  LoginRequest,
  LoginResponse,
  VerifyRequest,
  VerifyResponse,
  User,
  UpdateProfileRequest,
  Session,
  SessionsResponse,
  CreateUserRequest,
  UpdateUserRequest,
  UsersListResponse,
  UserDetailResponse,
  UserFelixWorkerStatus,
  PaginationParams,
  Subscriber,
  CreateSubscriberRequest,
  UpdateSubscriberRequest,
  CheckDuplicatesRequest,
  CheckDuplicatesResponse,
  FillByINNResponse,
  FillBankByBIKResponse,
  PassportData,
  SubscriberSearchResponse,
  SubscriberListResponse,
  SubscriberVersionDiff,
  Application,
  ApplicationListResponse,
  CreateApplicationRequest,
  UpdateApplicationRequest,
  CreateAccessResponse,
  CompleteApplicationRequest,
  ApplicationDraft,
  CreateDraftRequest,
  UpdateDraftRequest,
  UserAuditLog,
  AvailableActionsResponse,
  ListAllAuditLogsResponse,
  AggregatedAuditIP,
  AggregatedAuditUser,
  UserVersionDiff,
  ApplicationVersion,
  ApplicationVersionDiff,
  FelixSyncRunRecord,
  FelixResultRecord,
  FelixAttemptRecord,
  FelixIntegratedFieldsResponse,
  FelixIntegrationListResponse,
  FelixSyncPreview,
  FelixTariffListResponse,
  FelixTariffGroupsResponse,
  FelixOmsOptionsResponse,
  FelixDeviceListResponse,
  SettingsResponse,
  SettingsVersionEntry,
  FelixOmsIntegrationSettings,
  ContractVersion,
  ContractAuditEntry,
  LKAccessRevealResponse,
  LKAccessAuditEntry,
  CalendarRequestsListData,
  CalendarMoveRequestBody,
  ServiceRequestFelixOmsStatus,
} from './types';

// Используем относительный путь для работы с текущим доменом
// Можно переопределить через NEXT_PUBLIC_API_URL в .env.local
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL || '';

class ApiError extends Error {
  constructor(
    public status: number,
    public code: number,
    message: string,
    public details?: { fields?: Array<{ field: string; error: string }> }
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private baseURL: string;
  private sessionToken: string | null = null;
  public clientId: string;

  constructor(baseURL: string = API_BASE_URL) {
    this.baseURL = baseURL;
    // Генерируем или получаем client_id из localStorage
    if (typeof window !== 'undefined') {
      try {
        let clientId = localStorage.getItem('client_id');
        if (!clientId) {
          clientId = this.generateClientId();
          localStorage.setItem('client_id', clientId);
        }
        this.clientId = clientId;
        
        // Восстанавливаем сессию из localStorage - критически важно для PWA
        this.sessionToken = localStorage.getItem('session_token');
      } catch (error) {
        // Обработка ошибок localStorage (например, в приватном режиме)
        console.error('Ошибка доступа к localStorage:', error);
        this.clientId = this.generateClientId();
        this.sessionToken = null;
      }
    } else {
      this.clientId = '';
    }
  }

  private generateClientId(): string {
    // Генерируем UUID v4
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  setSessionToken(token: string | null): void {
    this.sessionToken = token;
    if (typeof window !== 'undefined') {
      try {
        if (token) {
          localStorage.setItem('session_token', token);
        } else {
          localStorage.removeItem('session_token');
        }
      } catch (error) {
        // Обработка ошибок localStorage (например, в приватном режиме или при переполнении)
        console.error('Ошибка сохранения токена в localStorage:', error);
      }
    }
  }

  getSessionToken(): string | null {
    return this.sessionToken;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    // Генерируем X-Request-ID для трассировки
    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        ...options,
        headers,
      });

      // Обработка ошибок авторизации (401, 403) - удаляем токен
      if (response.status === 401 || response.status === 403) {
        this.setSessionToken(null);
      }

      const data: ApiResponse<T> = await response.json();

      // Принимаем коды 200 (OK) и 201 (Created) как успешные
      if (!response.ok || (data.status.code !== 200 && data.status.code !== 201)) {
        // Если ошибка авторизации, удаляем токен
        if (data.status.code === 401 || data.status.code === 403) {
          this.setSessionToken(null);
        }
        throw new ApiError(
          response.status,
          data.status.code,
          data.status.message,
          data.status.details || undefined
        );
      }

      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      // При сетевых ошибках не удаляем токен - может быть временная проблема
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  // Аутентификация
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await this.request<LoginResponse>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as LoginResponse;
  }

  async verify(data: VerifyRequest): Promise<VerifyResponse> {
    const response = await this.request<VerifyResponse>('/api/v1/auth/verify', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    
    const verifyResponse = response.data as VerifyResponse;
    this.setSessionToken(verifyResponse.session_token);
    
    return verifyResponse;
  }

  async logout(): Promise<void> {
    try {
      await this.request('/api/v1/auth/logout', {
        method: 'POST',
      });
    } finally {
      this.setSessionToken(null);
    }
  }

  // Профиль пользователя
  async getProfile(): Promise<User> {
    const response = await this.request<User>('/api/v1/users/profile', {
      method: 'GET',
    });
    return response.data as User;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<User> {
    const response = await this.request<User>('/api/v1/users/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data as User;
  }

  // Сессии
  async getSessions(): Promise<Session[]> {
    const response = await this.request<SessionsResponse>('/api/v1/users/sessions', {
      method: 'GET',
    });
    return (response.data as SessionsResponse).sessions;
  }

  async revokeSession(sessionId: string): Promise<void> {
    await this.request(`/api/v1/users/sessions/${sessionId}`, {
      method: 'DELETE',
    });
  }

  // Администрирование (требует роль admin)
  async getUsers(params?: PaginationParams): Promise<UsersListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);

    const query = queryParams.toString();
    const endpoint = `/api/v1/users${query ? `?${query}` : ''}`;
    
    const response = await this.request<UsersListResponse>(endpoint, {
      method: 'GET',
    });
    return response.data as UsersListResponse;
  }

  async createUser(data: CreateUserRequest): Promise<User> {
    const response = await this.request<User>('/api/v1/users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as User;
  }

  async getUser(userId: string): Promise<UserDetailResponse> {
    const response = await this.request<UserDetailResponse>(`/api/v1/users/${userId}`, {
      method: 'GET',
    });
    return response.data as UserDetailResponse;
  }

  async updateUser(userId: string, data: UpdateUserRequest): Promise<User> {
    const response = await this.request<User>(`/api/v1/users/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data as User;
  }

  async getUserVersions(userId: string): Promise<UserVersionDiff[]> {
    const response = await this.request<UserVersionDiff[]>(`/api/v1/users/${userId}/versions`, {
      method: 'GET',
    });
    return response.data as UserVersionDiff[];
  }

  async getUserAuditLogs(userId: string, actions?: string[]): Promise<UserAuditLog[]> {
    let url = `/api/v1/users/${userId}/audit-logs`;
    if (actions && actions.length > 0) {
      url += `?actions=${actions.join(',')}`;
    }
    const response = await this.request<UserAuditLog[]>(url, {
      method: 'GET',
    });
    return response.data as UserAuditLog[];
  }

  async getAvailableActions(userId: string): Promise<string[]> {
    const response = await this.request<AvailableActionsResponse>(`/api/v1/users/${userId}/audit-logs/actions`, {
      method: 'GET',
    });
    return (response.data as AvailableActionsResponse).actions;
  }

  async listAllAuditLogs(params?: {
    limit?: number;
    offset?: number;
    actions?: string[];
    user_id?: string | string[];
    ip_address?: string;
  }): Promise<ListAllAuditLogsResponse> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.offset != null) sp.set('offset', String(params.offset));
    if (params?.actions?.length) sp.set('actions', params.actions.join(','));
    if (params?.user_id != null) {
      const uid = params.user_id;
      sp.set('user_id', Array.isArray(uid) ? uid.join(',') : uid);
    }
    if (params?.ip_address != null && params.ip_address !== '') sp.set('ip_address', params.ip_address);
    const q = sp.toString() ? `?${sp.toString()}` : '';
    const response = await this.request<ListAllAuditLogsResponse>(`/api/v1/audit-logs${q}`, { method: 'GET' });
    return response.data as ListAllAuditLogsResponse;
  }

  async getAuditLogAggregatedIPs(params?: { limit?: number; offset?: number }): Promise<AggregatedAuditIP[]> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.offset != null) sp.set('offset', String(params.offset));
    const q = sp.toString() ? `?${sp.toString()}` : '';
    const response = await this.request<AggregatedAuditIP[]>(`/api/v1/audit-logs/ips${q}`, { method: 'GET' });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getAuditLogAggregatedUsers(params?: { limit?: number }): Promise<AggregatedAuditUser[]> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    const q = sp.toString() ? `?${sp.toString()}` : '';
    const response = await this.request<AggregatedAuditUser[]>(`/api/v1/audit-logs/users${q}`, { method: 'GET' });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getAuditLogActions(): Promise<string[]> {
    const response = await this.request<string[]>(`/api/v1/audit-logs/actions`, { method: 'GET' });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getUserSessions(userId: string): Promise<Session[]> {
    const response = await this.request<Session[]>(`/api/v1/users/${userId}/sessions`, {
      method: 'GET',
    });
    return Array.isArray(response.data) ? response.data : [];
  }

  async getUserFelixWorkerStatus(userId: string): Promise<UserFelixWorkerStatus> {
    const response = await this.request<UserFelixWorkerStatus>(`/api/v1/users/${userId}/felix-worker`, {
      method: 'GET',
    });
    return (response.data ?? { status: 'not_ready', enabled: false, eligible: false }) as UserFelixWorkerStatus;
  }

  async triggerUserFelixWorkerSync(userId: string): Promise<{ status: string }> {
    const response = await this.request<{ status: string }>(`/api/v1/users/${userId}/felix-sync/trigger`, {
      method: 'POST',
    });
    return (response.data ?? { status: 'queued' }) as { status: string };
  }

  async getUserFelixSync(userId: string): Promise<FelixSyncRunRecord[]> {
    const response = await this.request<FelixSyncRunRecord[]>(`/api/v1/users/${userId}/felix-sync`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixSyncRunRecord[];
  }

  async getUserFelixSyncResults(userId: string): Promise<FelixResultRecord[]> {
    const response = await this.request<FelixResultRecord[]>(`/api/v1/users/${userId}/felix-sync/results`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixResultRecord[];
  }

  async getUserFelixSyncAttempts(
    userId: string,
    params?: { entity_type?: string; limit?: number; offset?: number }
  ): Promise<{ data: FelixAttemptRecord[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.entity_type) search.set('entity_type', params.entity_type);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const q = search.toString();
    const response = await this.request<{ data: FelixAttemptRecord[]; total: number }>(
      `/api/v1/users/${userId}/felix-sync/attempts${q ? `?${q}` : ''}`,
      { method: 'GET' }
    );
    return { data: response.data?.data ?? [], total: response.data?.total ?? 0 };
  }

  async revokeAllSessionsExceptCurrent(userId: string, currentSessionId: string): Promise<void> {
    await this.request(`/api/v1/users/${userId}/sessions/revoke-all-except-current?current_session_id=${currentSessionId}`, {
      method: 'POST',
    });
  }

  async getUserApplications(userId: string, page?: number, limit?: number): Promise<Application[]> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page.toString());
    if (limit) queryParams.append('limit', limit.toString());
    const query = queryParams.toString();
    const endpoint = `/api/v1/users/${userId}/applications${query ? `?${query}` : ''}`;
    const response = await this.request<Application[]>(endpoint, {
      method: 'GET',
    });
    return response.data as Application[];
  }

  async getUserSubscribers(userId: string, page?: number, limit?: number): Promise<Subscriber[]> {
    const queryParams = new URLSearchParams();
    if (page) queryParams.append('page', page.toString());
    if (limit) queryParams.append('limit', limit.toString());
    const query = queryParams.toString();
    const endpoint = `/api/v1/users/${userId}/subscribers${query ? `?${query}` : ''}`;
    const response = await this.request<Subscriber[]>(endpoint, {
      method: 'GET',
    });
    const data = response.data;
    return Array.isArray(data) ? data : [];
  }

  async deleteUser(userId: string): Promise<void> {
    await this.request(`/api/v1/users/${userId}`, {
      method: 'DELETE',
    });
  }

  // Subscriber methods
  async createSubscriber(data: CreateSubscriberRequest): Promise<Subscriber> {
    const response = await this.request<Subscriber>('/api/v1/subscribers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as Subscriber;
  }

  async searchSubscribers(query: string, limit: number = 10): Promise<SubscriberSearchResponse> {
    const response = await this.request<SubscriberSearchResponse>(
      `/api/v1/subscribers/search?q=${encodeURIComponent(query)}&limit=${limit}`
    );
    return response.data as SubscriberSearchResponse;
  }

  async getSubscriber(id: string): Promise<Subscriber> {
    const response = await this.request<Subscriber>(`/api/v1/subscribers/${id}`);
    return response.data as Subscriber;
  }

  async updateSubscriber(id: string, data: UpdateSubscriberRequest): Promise<Subscriber> {
    const response = await this.request<Subscriber>(`/api/v1/subscribers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data as Subscriber;
  }

  async listSubscribers(params?: PaginationParams & { search?: string; client_type?: string }): Promise<SubscriberListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.search) queryParams.set('search', params.search);
    if (params?.client_type) queryParams.set('client_type', params.client_type);

    const query = queryParams.toString();
    const response = await this.request<SubscriberListResponse>(`/api/v1/subscribers${query ? `?${query}` : ''}`);
    return response.data as SubscriberListResponse;
  }

  async getSubscriberVersionsWithDiffs(subscriberId: string): Promise<SubscriberVersionDiff[]> {
    const response = await this.request<SubscriberVersionDiff[]>(`/api/v1/subscribers/${subscriberId}/versions/diffs`, {
      method: 'GET',
    });
    return response.data as SubscriberVersionDiff[];
  }

  async checkSubscriberDuplicates(data: CheckDuplicatesRequest): Promise<CheckDuplicatesResponse> {
    const response = await this.request<CheckDuplicatesResponse>('/api/v1/subscribers/check-duplicates', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as CheckDuplicatesResponse;
  }

  async recognizePassport(file: File): Promise<PassportData> {
    const formData = new FormData();
    formData.append('file', file);

    const url = `${this.baseURL}/api/v1/subscribers/recognize-passport`;
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: formData,
      });

      const data: ApiResponse<PassportData> = await response.json();

      if (!response.ok || data.status.code !== 200) {
        throw new ApiError(
          response.status,
          data.status.code,
          data.status.message,
          data.status.details || undefined
        );
      }

      return data.data as PassportData;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  async fillByINN(inn: string): Promise<FillByINNResponse> {
    const response = await this.request<FillByINNResponse>(
      `/api/v1/subscribers/fill-by-inn?inn=${encodeURIComponent(inn)}`,
      { method: 'GET' }
    );
    return response.data as FillByINNResponse;
  }

  async fillByBIK(bik: string): Promise<FillBankByBIKResponse> {
    const response = await this.request<FillBankByBIKResponse>(
      `/api/v1/subscribers/fill-by-bik?bik=${encodeURIComponent(bik)}`,
      { method: 'GET' }
    );
    return response.data as FillBankByBIKResponse;
  }

  // Application methods
  async createApplication(data: CreateApplicationRequest): Promise<Application> {
    const response = await this.request<Application>('/api/v1/applications', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as Application;
  }

  async getApplication(id: string): Promise<Application> {
    const response = await this.request<Application>(`/api/v1/applications/${id}`);
    return response.data as Application;
  }

  async listApplications(params?: PaginationParams & { status?: string; subscriber_id?: string; search?: string; created_by?: string; felix_tariff_id?: string }): Promise<ApplicationListResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.set('page', params.page.toString());
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.status) queryParams.set('status', params.status);
    if (params?.subscriber_id) queryParams.set('subscriber_id', params.subscriber_id);
    if (params?.search) queryParams.set('search', params.search);
    if (params?.created_by) queryParams.set('created_by', params.created_by);
    if (params?.felix_tariff_id) queryParams.set('felix_tariff_id', params.felix_tariff_id);

    const query = queryParams.toString();
    const response = await this.request<ApplicationListResponse>(`/api/v1/applications${query ? `?${query}` : ''}`);
    return response.data as ApplicationListResponse;
  }

  async updateApplication(id: string, data: UpdateApplicationRequest): Promise<Application> {
    const response = await this.request<Application>(`/api/v1/applications/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data as Application;
  }

  async createAccess(applicationId: string): Promise<CreateAccessResponse> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/create-access`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
      });

      const data: ApiResponse<CreateAccessResponse> = await response.json();

      // Специальная обработка: если код 500, но есть данные и сообщение содержит "(данные доступны)",
      // возвращаем данные без ошибки (это нормальное поведение)
      if (data.status.code === 500 && 
          data.status.message.includes('(данные доступны)') && 
          data.data) {
        return data.data as CreateAccessResponse;
      }

      // Для остальных случаев используем стандартную обработку
      if (!response.ok || (data.status.code !== 200 && data.status.code !== 201)) {
        throw new ApiError(
          response.status,
          data.status.code,
          data.status.message,
          data.status.details || undefined
        );
      }

      return data.data as CreateAccessResponse;
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  async generateContract(applicationId: string, contractData?: {
    contract_number?: string;
    contract_date?: string;
  }): Promise<{
    blob: Blob;
    contractNumber: string;
    contractDate: string;
  }> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/generate-contract`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(contractData || {}),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData: ApiResponse<unknown> = await response.json();
          throw new ApiError(
            response.status,
            errorData.status.code,
            errorData.status.message,
            errorData.status.details || undefined
          );
        } else {
          throw new ApiError(
            response.status,
            response.status,
            `Ошибка генерации договора: ${response.statusText}`,
            undefined
          );
        }
      }

      // Получаем метаданные из заголовков
      const contractNumber = response.headers.get('X-Contract-Number') || '';
      const contractDate = response.headers.get('X-Contract-Date') || '';

      return {
        blob: await response.blob(),
        contractNumber,
        contractDate,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  // Подписание договора (новый flow)
  // Генерирует договор с подписью, отправляет на email абонента, возвращает файл и фейковые доступы
  async signContract(applicationId: string, signature: string): Promise<{
    blob: Blob;
    contractNumber: string;
    contractDate: string;
    username: string;
    password: string;
  }> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/sign-contract`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ signature }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData: ApiResponse<unknown> = await response.json();
          throw new ApiError(
            response.status,
            errorData.status.code,
            errorData.status.message,
            errorData.status.details || undefined
          );
        } else {
          throw new ApiError(
            response.status,
            response.status,
            `Ошибка подписания договора: ${response.statusText}`,
            undefined
          );
        }
      }

      // Получаем метаданные из заголовков
      const contractNumber = response.headers.get('X-Contract-Number') || '';
      const contractDate = response.headers.get('X-Contract-Date') || '';
      const username = response.headers.get('X-Username') || '';
      const password = response.headers.get('X-Password') || '';

      return {
        blob: await response.blob(),
        contractNumber,
        contractDate,
        username,
        password,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  // Перегенерация договора (с подтверждением)
  async regenerateContract(applicationId: string, signature: string): Promise<{
    blob: Blob;
    contractNumber: string;
    contractDate: string;
    username: string;
    password: string;
  }> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/regenerate-contract`;
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'X-Client-ID': this.clientId,
    };

    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }

    const requestId = this.generateClientId();
    headers['X-Request-ID'] = requestId;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({ signature, confirm: true }),
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData: ApiResponse<unknown> = await response.json();
          throw new ApiError(
            response.status,
            errorData.status.code,
            errorData.status.message,
            errorData.status.details || undefined
          );
        } else {
          throw new ApiError(
            response.status,
            response.status,
            `Ошибка перегенерации договора: ${response.statusText}`,
            undefined
          );
        }
      }

      // Получаем метаданные из заголовков
      const contractNumber = response.headers.get('X-Contract-Number') || '';
      const contractDate = response.headers.get('X-Contract-Date') || '';
      const username = response.headers.get('X-Username') || '';
      const password = response.headers.get('X-Password') || '';

      return {
        blob: await response.blob(),
        contractNumber,
        contractDate,
        username,
        password,
      };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(500, 500, 'Network error', undefined);
    }
  }

  async completeApplication(applicationId: string, data: CompleteApplicationRequest): Promise<Application> {
    const response = await this.request<Application>(
      `/api/v1/applications/${applicationId}/complete`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
    return response.data as Application;
  }

  // Draft methods
  async createDraft(data: CreateDraftRequest): Promise<ApplicationDraft> {
    const response = await this.request<ApplicationDraft>('/api/v1/applications/drafts', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data as ApplicationDraft;
  }

  async getDrafts(): Promise<ApplicationDraft[]> {
    const response = await this.request<ApplicationDraft[]>('/api/v1/applications/drafts');
    return response.data as ApplicationDraft[];
  }

  async getDraft(id: string): Promise<ApplicationDraft> {
    const response = await this.request<ApplicationDraft>(`/api/v1/applications/drafts/${id}`);
    return response.data as ApplicationDraft;
  }

  async updateDraft(id: string, data: UpdateDraftRequest): Promise<ApplicationDraft> {
    const response = await this.request<ApplicationDraft>(`/api/v1/applications/drafts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response.data as ApplicationDraft;
  }

  async deleteDraft(id: string): Promise<void> {
    await this.request(`/api/v1/applications/drafts/${id}`, {
      method: 'DELETE',
    });
  }

  async getApplicationVersions(applicationId: string): Promise<ApplicationVersion[]> {
    const response = await this.request<ApplicationVersion[]>(`/api/v1/applications/${applicationId}/versions`, {
      method: 'GET',
    });
    return response.data as ApplicationVersion[];
  }

  async getApplicationVersionsWithDiffs(applicationId: string): Promise<ApplicationVersionDiff[]> {
    const response = await this.request<ApplicationVersionDiff[]>(`/api/v1/applications/${applicationId}/versions/diffs`, {
      method: 'GET',
    });
    return response.data as ApplicationVersionDiff[];
  }

  async getApplicationFelixSync(applicationId: string): Promise<FelixSyncRunRecord[]> {
    const response = await this.request<FelixSyncRunRecord[]>(`/api/v1/applications/${applicationId}/felix-sync`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixSyncRunRecord[];
  }

  /** Поля, отправленные в Felix по заявке (из обмена), с подписями. Отдельный эндпоинт, не нагружает GET заявки. */
  async getApplicationFelixIntegratedFields(applicationId: string): Promise<FelixIntegratedFieldsResponse> {
    const response = await this.request<FelixIntegratedFieldsResponse>(`/api/v1/applications/${applicationId}/felix-integrated-fields`, { method: 'GET' });
    return (response.data ?? { integrated_fields: [], fields_with_labels: [] }) as FelixIntegratedFieldsResponse;
  }

  async getApplicationFelixSyncResults(applicationId: string): Promise<FelixResultRecord[]> {
    const response = await this.request<FelixResultRecord[]>(`/api/v1/applications/${applicationId}/felix-sync/results`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixResultRecord[];
  }

  async getApplicationFelixSyncAttempts(
    applicationId: string,
    params?: { entity_type?: string; limit?: number; offset?: number }
  ): Promise<{ data: FelixAttemptRecord[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.entity_type) search.set('entity_type', params.entity_type);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const q = search.toString();
    const response = await this.request<{ data: FelixAttemptRecord[]; total: number }>(
      `/api/v1/applications/${applicationId}/felix-sync/attempts${q ? `?${q}` : ''}`,
      { method: 'GET' }
    );
    return { data: response.data?.data ?? [], total: response.data?.total ?? 0 };
  }

  async getApplicationFelixSyncPreview(applicationId: string): Promise<FelixSyncPreview> {
    const response = await this.request<FelixSyncPreview>(
      `/api/v1/applications/${applicationId}/felix-sync/preview`,
      { method: 'GET' }
    );
    return (response.data ?? { entities: [], eligible: false }) as FelixSyncPreview;
  }

  async triggerApplicationFelixSync(applicationId: string): Promise<{ status: string }> {
    const response = await this.request<{ status: string }>(
      `/api/v1/applications/${applicationId}/felix-sync/trigger`,
      { method: 'POST' }
    );
    return (response.data ?? { status: 'queued' }) as { status: string };
  }

  async getServiceRequestFelixOmsStatus(serviceRequestId: string): Promise<ServiceRequestFelixOmsStatus> {
    const response = await this.request<ServiceRequestFelixOmsStatus>(
      `/api/v1/service-requests/${serviceRequestId}/felix-oms`,
      { method: 'GET' }
    );
    return (response.data ?? { status: 'not_ready', enabled: false, eligible: false }) as ServiceRequestFelixOmsStatus;
  }

  async triggerServiceRequestFelixOmsSync(serviceRequestId: string): Promise<{ status: string }> {
    const response = await this.request<{ status: string }>(
      `/api/v1/service-requests/${serviceRequestId}/felix-oms/trigger`,
      { method: 'POST' }
    );
    return (response.data ?? { status: 'queued' }) as { status: string };
  }

  /** Список сохранённых версий договора по заявке (только админ) */
  async listContractVersions(applicationId: string): Promise<ContractVersion[]> {
    const response = await this.request<ContractVersion[]>(
      `/api/v1/applications/${applicationId}/contracts/versions`,
      { method: 'GET' }
    );
    return (response.data ?? []) as ContractVersion[];
  }

  /** Скачать файл договора по версии (только админ). Возвращает blob и имя файла. */
  async downloadContractVersion(
    applicationId: string,
    contractVersionId: string
  ): Promise<{ blob: Blob; filename: string }> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/contracts/versions/${encodeURIComponent(contractVersionId)}/download`;
    const headers: Record<string, string> = {
      Accept: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'X-Client-ID': this.clientId,
    };
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    headers['X-Request-ID'] = this.generateClientId();

    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('application/json')) {
        const err = (await response.json()) as ApiResponse<unknown>;
        throw new ApiError(
          response.status,
          err.status.code,
          err.status.message,
          err.status.details || undefined
        );
      }
      throw new ApiError(
        response.status,
        response.status,
        `Ошибка скачивания: ${response.statusText}`,
        undefined
      );
    }
    const blob = await response.blob();
    const disposition = response.headers.get('Content-Disposition');
    const filename =
      disposition?.match(/filename="?([^";]+)"?/)?.[1]?.trim() || 'contract.docx';
    return { blob, filename };
  }

  /** Журнал аудита операций с договорами по заявке (только админ) */
  async listContractAudit(
    applicationId: string,
    params?: { limit?: number; offset?: number; action?: 'download' | 'list_versions' | 'upload' }
  ): Promise<ContractAuditEntry[]> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.offset != null) sp.set('offset', String(params.offset));
    if (params?.action) sp.set('action', params.action);
    const q = sp.toString() ? `?${sp.toString()}` : '';
    const response = await this.request<ContractAuditEntry[]>(
      `/api/v1/applications/${applicationId}/contracts/audit${q}`,
      { method: 'GET' }
    );
    return (response.data ?? []) as ContractAuditEntry[];
  }

  /** Раскрыть доступы ЛК по заявке (только админ, пишет в аудит) */
  async revealLKAccess(applicationId: string): Promise<LKAccessRevealResponse> {
    const response = await this.request<LKAccessRevealResponse>(
      `/api/v1/applications/${applicationId}/lk-access/reveal`,
      { method: 'POST' }
    );
    return response.data as LKAccessRevealResponse;
  }

  /** Журнал аудита раскрытия реквизитов ЛК по заявке (только админ) */
  async listLKAccessAudit(
    applicationId: string,
    params?: { limit?: number; offset?: number }
  ): Promise<LKAccessAuditEntry[]> {
    const sp = new URLSearchParams();
    if (params?.limit != null) sp.set('limit', String(params.limit));
    if (params?.offset != null) sp.set('offset', String(params.offset));
    const q = sp.toString() ? `?${sp.toString()}` : '';
    const response = await this.request<LKAccessAuditEntry[]>(
      `/api/v1/applications/${applicationId}/lk-access/audit${q}`,
      { method: 'GET' }
    );
    return (response.data ?? []) as LKAccessAuditEntry[];
  }

  /** Скачать подпись по версии договора (только админ), возвращает blob image/png */
  async downloadContractSignature(
    applicationId: string,
    contractVersionId: string
  ): Promise<Blob> {
    const url = `${this.baseURL}/api/v1/applications/${applicationId}/contracts/versions/${encodeURIComponent(contractVersionId)}/signature`;
    const headers: Record<string, string> = {
      Accept: 'image/png',
      'X-Client-ID': this.clientId,
    };
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    headers['X-Request-ID'] = this.generateClientId();
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new ApiError(
        response.status,
        response.status,
        `Ошибка скачивания подписи: ${response.statusText}`,
        undefined
      );
    }
    return response.blob();
  }

  async getSubscriberFelixSync(subscriberId: string): Promise<FelixSyncRunRecord[]> {
    const response = await this.request<FelixSyncRunRecord[]>(`/api/v1/subscribers/${subscriberId}/felix-sync`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixSyncRunRecord[];
  }

  /** Поля, отправленные в Felix по абоненту (из обмена), с подписями. Отдельный эндпоинт, не нагружает GET абонента. */
  async getSubscriberFelixIntegratedFields(subscriberId: string): Promise<FelixIntegratedFieldsResponse> {
    const response = await this.request<FelixIntegratedFieldsResponse>(`/api/v1/subscribers/${subscriberId}/felix-integrated-fields`, { method: 'GET' });
    return (response.data ?? { integrated_fields: [], fields_with_labels: [] }) as FelixIntegratedFieldsResponse;
  }

  async getSubscriberFelixSyncResults(subscriberId: string): Promise<FelixResultRecord[]> {
    const response = await this.request<FelixResultRecord[]>(`/api/v1/subscribers/${subscriberId}/felix-sync/results`, {
      method: 'GET',
    });
    return (response.data ?? []) as FelixResultRecord[];
  }

  async getSubscriberFelixSyncAttempts(
    subscriberId: string,
    params?: { entity_type?: string; limit?: number; offset?: number }
  ): Promise<{ data: FelixAttemptRecord[]; total: number }> {
    const search = new URLSearchParams();
    if (params?.entity_type) search.set('entity_type', params.entity_type);
    if (params?.limit != null) search.set('limit', String(params.limit));
    if (params?.offset != null) search.set('offset', String(params.offset));
    const q = search.toString();
    const response = await this.request<{ data: FelixAttemptRecord[]; total: number }>(
      `/api/v1/subscribers/${subscriberId}/felix-sync/attempts${q ? `?${q}` : ''}`,
      { method: 'GET' }
    );
    return { data: response.data?.data ?? [], total: response.data?.total ?? 0 };
  }

  async getFelixIntegrationsList(params?: {
    page?: number;
    limit?: number;
    source_table?: string;
    source_id?: string;
    date_from?: string;
    date_to?: string;
    status?: 'success' | 'partial' | 'failed' | 'skipped';
  }): Promise<FelixIntegrationListResponse> {
    const searchParams = new URLSearchParams();
    if (params?.page != null) searchParams.set('page', String(params.page));
    if (params?.limit != null) searchParams.set('limit', String(params.limit));
    if (params?.source_table) searchParams.set('source_table', params.source_table);
    if (params?.source_id) searchParams.set('source_id', params.source_id);
    if (params?.date_from) searchParams.set('date_from', params.date_from);
    if (params?.date_to) searchParams.set('date_to', params.date_to);
    if (params?.status) searchParams.set('status', params.status);
    const qs = searchParams.toString();
    const url = `/api/v1/integrations/felix${qs ? `?${qs}` : ''}`;
    const response = await this.request<FelixIntegrationListResponse>(url, { method: 'GET' });
    return response.data ?? { data: [], total: 0, page: 1, limit: 20, pages: 0 };
  }

  /** Список тарифов Felix (bl_billing.tariff.get). Ответ — сырой JSON Felix (errno, tariff), не ApiResponse. */
  async getFelixTariffs(): Promise<FelixTariffListResponse> {
    const url = `${this.baseURL}/api/v1/integrations/felix/tariffs`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Client-ID': this.clientId,
    };
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    headers['X-Request-ID'] = this.generateClientId();
    const response = await fetch(url, { method: 'GET', headers });
    const body = await response.json();
    if (!response.ok) {
      const api = body as ApiResponse<unknown>;
      if (api?.status?.code != null) {
        if (response.status === 401 || response.status === 403) {
          this.setSessionToken(null);
        }
        throw new ApiError(
          response.status,
          api.status.code,
          api.status.message ?? response.statusText,
          api.status.details ?? undefined
        );
      }
      throw new ApiError(response.status, response.status, response.statusText || 'Ошибка запроса к Felix', undefined);
    }
    return body as FelixTariffListResponse;
  }

  /** Настройки: GET */
  async getSettings(): Promise<SettingsResponse> {
    const response = await this.request<SettingsResponse>('/api/v1/settings', { method: 'GET' });
    const data = response.data;
    const felixOmsDefaults: FelixOmsIntegrationSettings = {
      enabled: false,
      order_type_id: '',
      service_request_order_type_id: '',
      contact_type_id: '',
      worker_id: '0',
      customer_type: '1',
      default_address_id: '',
    };
    const rawFelixOms = (data?.felix_oms_integration ?? {}) as Partial<FelixOmsIntegrationSettings> & {
      api_path?: string;
    };
    const { api_path: _ignoredApiPath, ...felixOmsFromServer } = rawFelixOms;
    return {
      selected_tariff_group_ids: data?.selected_tariff_group_ids ?? [],
      felix_oms_integration: {
        ...felixOmsDefaults,
        ...felixOmsFromServer,
      },
      version: data?.version,
    };
  }

  /** Настройки: PUT (только администратор) */
  async putSettings(data: {
    selected_tariff_group_ids?: string[];
    felix_oms_integration?: FelixOmsIntegrationSettings;
  }): Promise<void> {
    await this.request('/api/v1/settings', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /** История версий настроек (GET /api/v1/settings/versions), только администратор */
  async getSettingsVersions(): Promise<SettingsVersionEntry[]> {
    const response = await this.request<SettingsVersionEntry[]>('/api/v1/settings/versions', { method: 'GET' });
    return response.data ?? [];
  }

  /** Список устройств NMS (IP коммутаторов) из Felix (bl_nms.device.get) */
  async getFelixDevices(): Promise<FelixDeviceListResponse> {
    const url = `${this.baseURL}/api/v1/integrations/felix/devices`;
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'X-Client-ID': this.clientId,
    };
    if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    headers['X-Request-ID'] = this.generateClientId();
    const response = await fetch(url, { method: 'GET', headers });
    const body = await response.json();
    if (!response.ok) {
      const api = body as ApiResponse<unknown>;
      if (api?.status?.code != null) {
        if (response.status === 401 || response.status === 403) {
          this.setSessionToken(null);
        }
        throw new ApiError(
          response.status,
          api.status.code,
          api.status.message ?? response.statusText,
          api.status.details ?? undefined
        );
      }
      throw new ApiError(response.status, response.status, response.statusText || 'Ошибка запроса к Felix', undefined);
    }
    return body as FelixDeviceListResponse;
  }

  /** Список групп тарифов Felix (папок) для настройки выбора */
  async getFelixTariffGroups(): Promise<FelixTariffGroupsResponse> {
    const response = await this.request<FelixTariffGroupsResponse>('/api/v1/integrations/felix/tariff-groups', { method: 'GET' });
    return {
      groups: response.data?.groups ?? [],
      tariffs: response.data?.tariffs ?? [],
    };
  }

  /** Справочники OMS Felix для настроек */
  async getFelixOmsOptions(): Promise<FelixOmsOptionsResponse> {
    const response = await this.request<FelixOmsOptionsResponse>('/api/v1/integrations/felix/oms-options', { method: 'GET' });
    return {
      order_types: response.data?.order_types ?? [],
      admins: response.data?.admins ?? [],
      workers: response.data?.workers ?? [],
    };
  }

  /** Заявки с плановой датой в диапазоне (календарь) */
  async getCalendarRequests(params: {
    from: string;
    to: string;
    techIds?: string[];
    subscriberId?: string;
    /** Пустой массив = не отдавать подключения; undefined = все статусы (без query). */
    connectionStatus?: string[];
    /** Пустой массив = не отдавать сервис; undefined = все статусы. */
    serviceStatus?: string[];
  }): Promise<CalendarRequestsListData> {
    const sp = new URLSearchParams();
    sp.set('from', params.from);
    sp.set('to', params.to);
    if (params.subscriberId) sp.set('subscriber_id', params.subscriberId);
    for (const id of params.techIds ?? []) {
      if (id) sp.append('tech_ids', id);
    }
    if (params.connectionStatus !== undefined) {
      if (params.connectionStatus.length === 0) sp.append('connection_status', '');
      else for (const s of params.connectionStatus) if (s) sp.append('connection_status', s);
    }
    if (params.serviceStatus !== undefined) {
      if (params.serviceStatus.length === 0) sp.append('service_status', '');
      else for (const s of params.serviceStatus) if (s) sp.append('service_status', s);
    }
    const response = await this.request<CalendarRequestsListData>(
      `/api/v1/calendar/requests?${sp.toString()}`,
      { method: 'GET' }
    );
    return { events: response.data?.events ?? [] };
  }

  async moveCalendarRequest(body: CalendarMoveRequestBody): Promise<void> {
    await this.request<Record<string, unknown>>('/api/v1/calendar/requests/move', {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }
}

// Экспортируем singleton экземпляр
export const apiClient = new ApiClient();
export default apiClient;
